import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';
import {
  formatPrice,
  formatRegularPrice,
  hasAntiWastePrice,
  type Product,
} from '../core/domain/catalog';
import { CtChalkTag } from './chalk-tag.directive';

/**
 * Prix produit côté client : en anti-gaspi, le tarif catalogue est barré
 * et le prix remisé prend le relais — les deux sur l’ardoise, sur deux lignes.
 */
@Component({
  selector: 'ct-product-price',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CtChalkTag],
  template: `
    @if (variant() === 'chalk') {
      <span ctChalkTag [small]="small()" class="slate" [class.slate--stack]="discounted()">
        @if (discounted()) {
          <s class="was">{{ regular() }}</s>
        }
        <span class="now">{{ current() }}</span>
      </span>
    } @else {
      <span class="hero" [class.hero--stack]="discounted()">
        @if (discounted()) {
          <s class="was">{{ regular() }}</s>
        }
        <span class="now">{{ current() }}</span>
      </span>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      flex-shrink: 0;
    }

    .slate {
      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.1rem;
      line-height: 1.15;
    }

    .slate--stack {
      padding-block: 0.35rem;
    }

    .hero {
      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.15rem;
      font-size: inherit;
      font-weight: inherit;
    }

    .was {
      color: var(--ct-text-muted);
      font-size: 0.78em;
      font-weight: var(--ct-weight-regular);
      text-decoration: line-through;
      text-decoration-thickness: 1.5px;
    }

    .hero .was {
      font-size: var(--ct-size-base);
    }

    .now {
      font-size: inherit;
      font-weight: inherit;
      color: inherit;
    }

    .hero--stack .now {
      color: var(--ct-or-recolte-dark);
    }
  `,
})
export class CtProductPrice {
  readonly product = input.required<Product>();
  /** `chalk` pour les listes ; `hero` pour le prix principal d’une fiche. */
  readonly variant = input<'chalk' | 'hero'>('chalk');
  readonly small = input(false, { transform: booleanAttribute });

  protected readonly discounted = computed(() => hasAntiWastePrice(this.product()));
  protected readonly regular = computed(() => formatRegularPrice(this.product()));
  protected readonly current = computed(() => formatPrice(this.product()));
}
